import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { useTranslation } from 'react-i18next';
import EnterprisesEmptyState from '../../components/EnterprisesEmptyState';
import { useNavigate } from 'react-router-dom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
  LOCKED_COLUMN,
} from './data';
import { BODY_CONTENT_GENERATE } from './components/EnterprisesDealTableCells';
import { Enterprise } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import {
  getEnterpriseRoute,
  getHasActiveFilters,
  getIsArrayEmpty,
} from 'utils';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMatchMedia from 'hooks/useMatchMedia';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import useTableFetchState from 'hooks/useTableFetchState';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useEnterprisesListFilters from './hooks/useEnterprisesListFilters';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import ManageColumns from 'components/ManageColumns';

import './EntrprisesList.styles.scss';

type EntrprisesListProps = {
  className?: string;
};

const EntrprisesList: React.FC<EntrprisesListProps> = (props) => {
  const { className } = props;

  const {
    isLoading,
    totalPages,
    totalItems,
    data: enterprises,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<Enterprise>();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.ENTERPRISES_LIST,
    TABLE_COLUMNS,
  );

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { params } = useQueryParams();

  const { page, sort, name, principal } = params;

  const {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  } = useEnterprisesListFilters();

  const { popServerError } = useContext(PopNotificationsContext);

  const isEmpty = !isLoading && getIsArrayEmpty(enterprises);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const classes = classNames('enterprises-list', className);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const navigate = useNavigate();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (enterprise: Enterprise) =>
            BODY_CONTENT_GENERATE[col]({
              enterprise,
            }),
        }),
        {},
      ),
    [],
  );

  const table = useMemo(
    () => (
      <ReorderResizeTable
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        columns={columns as any}
        sortingValues={SORTING_VALUES}
        columnFilterOptions={columnFilterOptions}
        isDataLoading={isLoading}
        totalPages={totalPages}
        totalItems={totalItems}
        recordName="enterprise"
        data={enterprises}
        customSizes={CUSTOM_COL_SIZES}
        onReorder={onReorder as (columns: Array<keyof Enterprise>) => void}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onRowClick={({ _id, isRWC, isDemo }) =>
          navigate(getEnterpriseRoute(isRWC, isDemo, _id, 'analytics'))
        }
      />
    ),
    [
      enterprises,
      isLoading,
      columns,
      renderedContent,
      columnFilterOptions,
      totalPages,
      totalItems,
      onReorder,
      columnSizing,
      onColumnSizingChange,
      navigate,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.enterprises.getEnterprises({
          $sort: sort,
          $page: page,
          $limit: limit,
          name,
          principal,
        });
        setPaginatedData(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    sort,
    page,
    limit,
    name,
    principal,
    popServerError,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <div className={classes}>
      <div className="admin-dashboard-router__heading">
        <h1>{t('manageEnterprises')}</h1>
        <div className="enterprises-list__filters">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
          <Button icon={PluscircleIcon} onClick={() => navigate('add')}>
            {t(isPhablet ? 'enterprise' : 'addAnEnterprise')}
          </Button>
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      {isLoading && <Loader />}
      {isEmpty ? <EnterprisesEmptyState /> : table}
    </div>
  );
};

export default EntrprisesList;
